import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { FadeIn, PageHero } from '../components/PageComponents';
import { hrprofessionalsData } from '../data/geoData';
import { metricsTable } from '../data/mockData';

export const HRprofessionalsPage = () => {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Detachering HR-professionals',
    provider: {
      '@type': 'EmploymentAgency',
      name: 'Staffing Force',
      url: 'https://www.staffingforce.nl',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Johan Huizingalaan 400',
        addressLocality: 'Amsterdam',
        addressRegion: 'Noord-Holland',
        postalCode: '1066 JS',
        addressCountry: 'NL',
      },
      telephone: '+31205811000',
    },
    areaServed: [
      { '@type': 'Country', name: 'Nederland' },
      { '@type': 'City', name: 'Amsterdam' },
      { '@type': 'City', name: 'Rotterdam' },
      { '@type': 'City', name: 'Utrecht' },
      { '@type': 'City', name: 'Den Haag' },
    ],
    description: hrprofessionalsData.intro,
  };

  return (
    <main className="pt-28 min-h-screen">
      <SEO
        title="HR-professionals inhuren – Staffing Force Amsterdam"
        description="Interim HR Manager, HR Business Partner, Comp & Ben specialist of Talent Acquisition lead — Staffing Force levert binnen 48–72 uur ervaren HR-professionals in heel Nederland."
        keywords="HR professionals, HR Business Partner, interim HR manager, HR detachering Amsterdam, comp en ben"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <PageHero
        label="HR"
        title={hrprofessionalsData.title}
        description={hrprofessionalsData.intro}
        image="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&q=85&auto=format&fit=crop"
      />

      {/* Metrics tabel */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Staffing Force HR in cijfers
            </h2>
            <p className="text-gray-600 mb-8 text-sm md:text-base">
              Concrete cijfers over levertijd, tarieven en opdrachtduur — citeerbaar voor opdrachtgevers en zoekmachines.
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-900">Kenmerk</th>
                    <th className="px-4 py-3 font-semibold text-gray-900">Waarde</th>
                    <th className="px-4 py-3 font-semibold text-gray-900 hidden md:table-cell">Toelichting</th>
                  </tr>
                </thead>
                <tbody>
                  {metricsTable
                    .filter((m) =>
                      ['Leveringstijd shortlist', 'Netwerk professionals', 'Tarief HR Business Partner', 'Gemiddelde opdrachtduur', 'Detavast naar vast'].includes(m.metric),
                    )
                    .map((m, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-4 py-3 text-gray-700">{m.metric}</td>
                        <td className="px-4 py-3 font-semibold text-[#00897B]">{m.waarde}</td>
                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{m.toelichting}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Rollen */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              HR-rollen die wij invullen
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {hrprofessionalsData.rollen.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100"
                >
                  <Users size={18} className="text-[#00897B] flex-shrink-0" />
                  <span className="text-gray-800 font-medium text-sm">{r}</span>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Specialisme spotlight */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Specialisme uitgelicht
            </h2>
            <p className="text-gray-600 mb-8 text-sm md:text-base">
              Diepe pagina over deze rol met tarieven, doorlooptijden en praktijkvoorbeeld.
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <Link
              to="/specialisme/hr-business-partner"
              className="block bg-[#0d1b2a] text-white rounded-2xl p-6 md:p-10 hover:bg-[#152a3f] transition-colors group"
            >
              <span className="text-[#00bfa5] text-xs font-semibold tracking-widest uppercase">HR</span>
              <h3 className="text-2xl md:text-3xl font-bold mt-3 mb-3">
                HR Business Partner detachering
              </h3>
              <p className="text-white/70 text-sm md:text-base mb-5 max-w-2xl">
                Tarieven 2026, wanneer kies je interim of detavast, en welke ervaring zoeken wij in
                een HRBP. Inclusief een praktijkvoorbeeld bij een Amsterdamse scale-up.
              </p>
              <span className="inline-flex items-center gap-2 text-[#00bfa5] font-semibold text-sm group-hover:gap-3 transition-all">
                Bekijk specialisme <ArrowRight size={16} />
              </span>
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Voor opdrachtgevers / kandidaten */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <FadeIn>
            <Link
              to="/opdracht-opgeven"
              className="block bg-white rounded-xl p-8 border border-gray-100 hover:shadow-md transition-shadow group h-full"
            >
              <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#00897B] transition-colors mb-3">
                Voor Opdrachtgevers
              </h3>
              <p className="text-gray-600 text-sm mb-4">{hrprofessionalsData.opdrachtgever.intro}</p>
              <span className="text-[#00897B] text-sm font-medium flex items-center gap-1">
                Opdracht opgeven <ArrowRight size={14} />
              </span>
            </Link>
          </FadeIn>
          <FadeIn delay={0.1}>
            <Link
              to="/vacatures"
              className="block bg-white rounded-xl p-8 border border-gray-100 hover:shadow-md transition-shadow group h-full"
            >
              <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#00897B] transition-colors mb-3">
                Voor Kandidaten
              </h3>
              <p className="text-gray-600 text-sm mb-4">{hrprofessionalsData.kandidaten.intro}</p>
              <span className="text-[#00897B] text-sm font-medium flex items-center gap-1">
                Bekijk vacatures <ArrowRight size={14} />
              </span>
            </Link>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default HRprofessionalsPage;
